import { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { AdminTableSkeleton } from '../components/Skeleton';
import './Admin.css';

function ActiveUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [flash, setFlash] = useState(null);

  // Create user form
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', username: '', role: 'staff' });
  const [createLoading, setCreateLoading] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);

  const fetchUsers = async () => {
    try {
      const data = await api.get('/api/admin/users');
      setUsers(data.users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.username.trim()) {
      setCreateMsg({ type: 'error', text: 'All fields are required.' });
      return;
    }

    setCreateLoading(true);
    setCreateMsg(null);

    try {
      const data = await api.post('/api/admin/users', form);
      setCreateMsg({ type: 'success', text: data.message });
      setUsers(prev => [data.user, ...prev]);
      setForm({ firstName: '', lastName: '', email: '', username: '', role: 'staff' });
      setTimeout(() => {
        setShowCreateModal(false);
        setCreateMsg(null);
      }, 2000);
    } catch (err) {
      setCreateMsg({ type: 'error', text: err.response?.data?.error?.message || 'Failed to create user.' });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setActionLoading(true);
    try {
      await api.put(`/api/admin/users/${deactivateTarget.id}/deactivate`);
      setUsers(prev => prev.filter(u => u.id !== deactivateTarget.id));
      setDeactivateTarget(null);
    } catch (err) {
      console.error('Deactivate failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetLoading(true);
    setFlash(null);
    try {
      const data = await api.post(`/api/admin/users/${resetTarget.id}/reset-password`);
      setFlash({
        type: 'success',
        text: data.message || `A password reset link has been sent to ${resetTarget.email}.`
      });
      setResetTarget(null);
    } catch (err) {
      setFlash({
        type: 'error',
        text: err.response?.data?.error?.message || 'Failed to send the reset link. Please try again.'
      });
      setResetTarget(null);
    } finally {
      setResetLoading(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateMsg(null);
    setForm({ firstName: '', lastName: '', email: '', username: '', role: 'staff' });
  };

  if (loading) {
    return <AdminTableSkeleton columns={7} rows={6} />;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Active Users</h1>
          <p>Manage user accounts and permissions</p>
        </div>
        <button className="admin-create-btn" onClick={() => setShowCreateModal(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create User
        </button>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search"
            placeholder="Search by name, username, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" onClick={() => setSearchQuery('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <span className="admin-count">
          {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
        </span>
      </div>

      {flash && (
        <div className={`admin-flash ${flash.type}`}>
          {flash.type === 'success' ? (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          <span>{flash.text}</span>
          <button className="admin-flash-close" onClick={() => setFlash(null)} aria-label="Dismiss">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      <div className="admin-table-card">
        {filteredUsers.length === 0 ? (
          <div className="admin-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
            <h3>No users found</h3>
            <p>Try adjusting your search or create a new user</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>MFA</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td className="admin-name-cell">
                    <div className="admin-avatar">{user.firstName[0]}{user.lastName[0]}</div>
                    <span>{user.firstName} {user.lastName}</span>
                  </td>
                  <td>{user.username}</td>
                  <td className="admin-email-cell">{user.email}</td>
                  <td>
                    <span className={`admin-role-badge ${user.role}`}>{user.role}</span>
                  </td>
                  <td>
                    <span className={`admin-mfa-badge ${user.mfaEnabled ? 'enabled' : 'disabled'}`}>
                      {user.mfaEnabled ? 'Enabled' : 'Off'}
                    </span>
                  </td>
                  <td className="admin-date-cell">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="admin-row-actions">
                      <button
                        className="admin-icon-btn reset-password"
                        data-tooltip="Reset password"
                        aria-label={`Reset password for ${user.firstName} ${user.lastName}`}
                        onClick={() => setResetTarget(user)}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                        </svg>
                      </button>
                      <button
                        className="admin-icon-btn deactivate"
                        data-tooltip="Deactivate"
                        aria-label={`Deactivate ${user.firstName} ${user.lastName}`}
                        onClick={() => setDeactivateTarget(user)}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="8.5" cy="7" r="4" />
                          <line x1="18" y1="8" x2="23" y2="13" />
                          <line x1="23" y1="8" x2="18" y2="13" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <div className="admin-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
              </div>
              <div className="admin-modal-header-text">
                <h2>Create User Account</h2>
                <p>A temporary password will be emailed to the user</p>
              </div>
            </div>

            {createMsg && (
              <div className={`admin-modal-alert ${createMsg.type}`} style={{ marginBottom: 18 }}>
                {createMsg.type === 'success' ? (
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                <span>{createMsg.text}</span>
              </div>
            )}

            <form onSubmit={handleCreateUser} className="admin-modal-form">
              <div className="admin-modal-row">
                <div className="admin-modal-field">
                  <label htmlFor="create-firstName">First Name</label>
                  <input
                    id="create-firstName"
                    type="text"
                    className="admin-modal-input"
                    placeholder="First name"
                    value={form.firstName}
                    onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                  />
                </div>
                <div className="admin-modal-field">
                  <label htmlFor="create-lastName">Last Name</label>
                  <input
                    id="create-lastName"
                    type="text"
                    className="admin-modal-input"
                    placeholder="Last name"
                    value={form.lastName}
                    onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  />
                </div>
              </div>

              <div className="admin-modal-field">
                <label htmlFor="create-email">Email Address</label>
                <input
                  id="create-email"
                  type="email"
                  className="admin-modal-input"
                  placeholder="user@example.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>

              <div className="admin-modal-row">
                <div className="admin-modal-field">
                  <label htmlFor="create-username">Username</label>
                  <input
                    id="create-username"
                    type="text"
                    className="admin-modal-input"
                    placeholder="username"
                    value={form.username}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  />
                </div>
                <div className="admin-modal-field">
                  <label htmlFor="create-role">Role</label>
                  <select
                    id="create-role"
                    className="admin-modal-select"
                    value={form.role}
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  >
                    <option value="staff">Staff</option>
                    <option value="billing">Billing</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-modal-btn-cancel" onClick={closeCreateModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-modal-btn-submit" disabled={createLoading}>
                  {createLoading && <span className="admin-modal-spinner" />}
                  {createLoading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation */}
      {resetTarget && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Reset Password?</h3>
            <p>
              This will email <strong>{resetTarget.firstName} {resetTarget.lastName}</strong> ({resetTarget.email})
              a secure link to choose a new password. Their current password keeps working until they use it.
            </p>
            <div className="admin-confirm-note">
              <ul>
                <li>The link expires in 60 minutes and can only be used once.</li>
                <li>Once they set a new password, they&apos;ll be signed out on all devices.</li>
                <li>You will not see or set their password.</li>
              </ul>
            </div>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setResetTarget(null)} disabled={resetLoading}>
                Cancel
              </button>
              <button className="admin-confirm-primary" onClick={handleResetPassword} disabled={resetLoading}>
                {resetLoading && <span className="admin-modal-spinner" />}
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation */}
      {deactivateTarget && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Deactivate User?</h3>
            <p>
              This will prevent <strong>{deactivateTarget.firstName} {deactivateTarget.lastName}</strong> ({deactivateTarget.username})
              from signing in. You can reactivate them later from the Deleted Users section.
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setDeactivateTarget(null)} disabled={actionLoading}>
                Cancel
              </button>
              <button className="admin-confirm-danger" onClick={handleDeactivate} disabled={actionLoading}>
                {actionLoading ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActiveUsers;
